import { Icon, Text, colors } from '@khaddi/ui';
import { useEffect, useMemo, useRef, useState } from 'react';
import { ActivityIndicator, Animated, PanResponder, Pressable, View, type GestureResponderEvent } from 'react-native';

const HEIGHT = 60;
const PAD = 6;
const KNOB = HEIGHT - PAD * 2;
const HOLD_MS = 800;
/** Share of the track the knob must travel before release counts as a confirmation. */
const THRESHOLD = 0.85;

export type SlideToConfirmProps = {
  label: string;
  onConfirm: () => void;
  loading?: boolean;
  disabled?: boolean;
};

/**
 * Drag the knob to the end to confirm. A deliberate gesture avoids marking an order
 * delivered by an accidental tap; keyboard users activate it with Enter/Space and
 * touch/mouse users can also press and hold.
 */
export const SlideToConfirm = ({ label, onConfirm, loading = false, disabled = false }: SlideToConfirmProps) => {
  const [trackWidth, setTrackWidth] = useState(0);
  const [hint, setHint] = useState(false);
  const offset = useRef(new Animated.Value(0)).current;
  const travel = Math.max(1, trackWidth - KNOB - PAD * 2);

  const live = useRef({ travel, onConfirm, blocked: disabled || loading });
  live.current = { travel, onConfirm, blocked: disabled || loading };

  const holdTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clearHold = () => {
    if (holdTimer.current) clearTimeout(holdTimer.current);
    holdTimer.current = null;
  };

  const settle = (to: number) => Animated.spring(offset, { toValue: to, useNativeDriver: false, bounciness: 4 }).start();

  const confirm = () => {
    if (live.current.blocked) return;
    clearHold();
    setHint(false);
    Animated.timing(offset, { toValue: live.current.travel, duration: 140, useNativeDriver: false }).start();
    live.current.onConfirm();
  };
  const confirmRef = useRef(confirm);
  confirmRef.current = confirm;

  const responder = useMemo(
    () =>
      PanResponder.create({
        onStartShouldSetPanResponder: () => !live.current.blocked,
        onMoveShouldSetPanResponder: () => !live.current.blocked,
        onPanResponderTerminationRequest: () => false,
        onPanResponderGrant: () => {
          holdTimer.current = setTimeout(() => confirmRef.current(), HOLD_MS);
        },
        onPanResponderMove: (_, gesture) => {
          if (Math.abs(gesture.dx) > 4) clearHold();
          offset.setValue(Math.min(live.current.travel, Math.max(0, gesture.dx)));
        },
        onPanResponderRelease: (_, gesture) => {
          const wasHolding = holdTimer.current !== null;
          clearHold();
          if (gesture.dx >= live.current.travel * THRESHOLD) confirmRef.current();
          else {
            if (wasHolding) setHint(true);
            settle(0);
          }
        },
        onPanResponderTerminate: () => {
          clearHold();
          settle(0);
        },
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [offset],
  );

  // A failed request leaves the screen in place: slide the knob back so it can be retried.
  useEffect(() => {
    if (!loading) settle(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  useEffect(() => clearHold, []);

  const onTrackPress = (event: GestureResponderEvent) => {
    // The track renders as a native <button>, so Enter/Space arrive as a click with detail 0.
    const native: object = event.nativeEvent;
    const fromKeyboard = ('key' in native) || ('detail' in native && native.detail === 0);
    if (fromKeyboard) confirm();
    else setHint(true);
  };

  const labelOpacity = offset.interpolate({ inputRange: [0, travel], outputRange: [1, 0.15], extrapolate: 'clamp' });

  return (
    <View style={{ gap: 6 }}>
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={label}
        accessibilityHint="Trượt nút sang phải, nhấn giữ, hoặc nhấn Enter để xác nhận"
        accessibilityState={{ disabled: disabled || loading, busy: loading }}
        disabled={disabled || loading}
        onPress={onTrackPress}
        onLongPress={confirm}
        delayLongPress={HOLD_MS}
        onLayout={(event) => setTrackWidth(event.nativeEvent.layout.width)}
        style={{ height: HEIGHT, borderRadius: HEIGHT / 2, backgroundColor: colors.primary, justifyContent: 'center', opacity: disabled ? 0.5 : 1, overflow: 'hidden' }}
      >
        <Animated.View style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: Animated.add(offset, KNOB + PAD * 2), backgroundColor: colors.primaryDark }} />
        <Animated.View style={{ opacity: labelOpacity, alignItems: 'center', paddingLeft: KNOB }}>
          <Text variant="bodyStrong" color={colors.onPrimary} style={{ fontSize: 15 }}>
            {label}
          </Text>
        </Animated.View>
        <Animated.View
          {...responder.panHandlers}
          // index.html sets touch-action:none on this id so mobile browsers don't scroll mid-drag
          nativeID="slide-to-confirm-knob"
          style={{
            position: 'absolute',
            left: PAD,
            top: PAD,
            width: KNOB,
            height: KNOB,
            borderRadius: KNOB / 2,
            backgroundColor: colors.surface,
            alignItems: 'center',
            justifyContent: 'center',
            cursor: disabled ? 'auto' : 'pointer',
            transform: [{ translateX: offset }],
          }}
        >
          {loading ? <ActivityIndicator color={colors.primaryText} /> : <Icon name="chevronRight" size={22} color={colors.primaryText} strokeWidth={2.6} />}
        </Animated.View>
      </Pressable>
      {hint ? (
        <Text variant="caption" style={{ textAlign: 'center' }}>
          Kéo nút tròn sang phải hết cỡ (hoặc nhấn giữ) để xác nhận
        </Text>
      ) : null}
    </View>
  );
};
